import { useTheme } from '@mui/material/styles';

/**
 * Returns accent colors derived from the active MUI theme palette.
 *
 * Replaces hardcoded ACCENT_COLORS for decorative elements (KPI cards,
 * chart fills, card borders, icon backgrounds) so that switching the
 * group color palette visibly changes the entire UI — not just buttons.
 *
 * Keys mirror ACCENT_COLORS so existing `color={accents.blue}` patterns
 * keep working with zero refactor at the call-site.
 */
export function usePaletteAccents() {
  const theme = useTheme();
  return {
    blue: theme.palette.primary.main,
    green: theme.palette.success.main,
    red: theme.palette.error.main,
    orange: theme.palette.warning.main,
    purple: theme.palette.secondary.main,
    teal: theme.palette.info.main,
    amber: theme.palette.warning.dark,
    indigo: theme.palette.primary.dark,
    pink: theme.palette.secondary.light,
    slate: theme.palette.text.secondary,
  };
}

/**
 * Ordered array of 6 visually distinct palette colors.
 * Use for cycling through KPI cards, chart series, etc.
 */
export function usePaletteArray() {
  const theme = useTheme();
  return [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
}

/**
 * Palette-derived chart colors — use instead of CHART_COLORS import.
 * Returns 6 colors suited for the current palette + light/dark mode.
 */
export function useChartColors() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return [
    isDark ? theme.palette.primary.light : theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
  ];
}

/**
 * Convert a hex color to rgba with given alpha (0–1).
 * Useful for dynamic palette rgba backgrounds.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
